'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { User, Lock, AlertCircle, X, CheckCircle, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { employeeLogin, verify2FA } from '@/services/auth';
import appLogger from '@/utils/logger';
import { getPostLoginRedirectPath } from '@/lib/postLoginRedirect';
import { getUserFacingApiMessage } from '@/lib/httpErrorMessage';

const TOTP_SETUP_TOKEN_KEY = 'chargehub_2fa_setup_temp_token';

const normalizeEmployeeToUser = (employee: { id: number | string; designation?: string; role?: string; firstName?: string; lastName?: string; email?: string; status?: string }) => {
  const resolvedRole = String(employee.designation || employee.role || 'Admin');
  return {
    id: String(employee.id),
    firstName: employee.firstName || '',
    lastName: employee.lastName || '',
    email: employee.email || '',
    mobile: '',
    role: resolvedRole,
    status: employee.status || 'Active'
  };
};

const LoginScreen: React.FC = () => {
  const { login, isAuthenticated, isLoading: authLoading, user } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordChangedMessage, setPasswordChangedMessage] = useState(false);
  // 2FA verification step (after password login when 2FA is enabled)
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);

  useEffect(() => {
    if (searchParams.get('passwordChanged') === '1') {
      setPasswordChangedMessage(true);
      router.replace('/', { scroll: false });
    }
  }, [searchParams, router]);

  /** Already signed in (e.g. opened `/` or used browser back): skip login UI and go to app */
  useEffect(() => {
    if (authLoading || !isAuthenticated || !user) return;
    router.replace(getPostLoginRedirectPath({ role: user.role, designation: user.role }));
  }, [authLoading, isAuthenticated, user, router]);

  // Clear any stale 2FA setup token when landing on login so we always use a fresh token after login
  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(TOTP_SETUP_TOKEN_KEY);
    }
  }, []);

  const handleLogin = useCallback(async () => {
    if (!username.trim() || !password) {
      setError('Please enter your username and password');
      return;
    }

    setError(null);
    try {
      setIsLoading(true);
      const data = await employeeLogin(username.trim(), password);

      if (data.kind === 'requires2FASetup') {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(TOTP_SETUP_TOKEN_KEY, data.tempToken);
        }
        router.push('/setup-2fa');
        return;
      }

      if (data.kind === 'requires2FA') {
        setTempToken(data.tempToken);
        setTotpCode('');
        setUseBackupCode(false);
        setIsLoading(false);
        return;
      }

      if (data.kind === 'success') {
        const normalizedUser = normalizeEmployeeToUser(data.employee);
        await login(data.accessToken, normalizedUser, data.refreshToken);
        router.replace(getPostLoginRedirectPath(data.employee));
      }
    } catch (error: unknown) {
      const errorMessage = getUserFacingApiMessage(error, 'Login failed. Please try again.');
      appLogger.errorWithContext(
        error instanceof Error ? error : new Error('Login failed'),
        'LoginScreen.handleLogin',
        {}
      );
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [username, password, login, router]);

  const handleVerify2FA = useCallback(async () => {
    const code = totpCode.replace(/\s/g, '').trim();
    if (!tempToken || !code) {
      setError(useBackupCode ? 'Enter a backup code.' : 'Enter the 6-digit code from your authenticator app.');
      return;
    }
    setError(null);
    try {
      setIsVerifying2FA(true);
      const data = await verify2FA(tempToken, code);
      const normalizedUser = normalizeEmployeeToUser(data.employee);
      await login(data.accessToken, normalizedUser, data.refreshToken);
      router.replace(getPostLoginRedirectPath(data.employee));
    } catch (error: unknown) {
      const errorMessage = getUserFacingApiMessage(error, 'Invalid code. Please try again.');
      setError(errorMessage);
      setTotpCode('');
    } finally {
      setIsVerifying2FA(false);
    }
  }, [tempToken, totpCode, useBackupCode, login, router]);

  const handleBackFrom2FA = useCallback(() => {
    setTempToken(null);
    setTotpCode('');
    setUseBackupCode(false);
    setError(null);
  }, []);

  if (!authLoading && isAuthenticated && user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center text-muted-foreground text-sm" role="status">
          Opening ChargeHub…
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-white">
      {/* Left — brand panel (tablet / desktop); pattern is CSS-only (no external assets) */}
      <div className="relative flex w-[60%] flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700 p-10">
        <div
          className="absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage: `repeating-linear-gradient(
              -35deg,
              transparent,
              transparent 14px,
              rgba(255, 255, 255, 0.04) 14px,
              rgba(255, 255, 255, 0.04) 15px
            )`,
          }}
          aria-hidden
        />

        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/40 to-gray-800/25" aria-hidden />
        
        {/* Logo and Branding */}
        <div className="absolute left-10 top-12 z-10 text-left">
          <Image
            src="/assets/images/Logo.png?v=2"
            alt="ChargeHub Logo"
            width={208}
            height={208}
            className="mb-0 h-52"
            priority
          />

          <div className="flex items-center mb-3">
            <span className="inline-block w-6 h-1 bg-green-500 rounded mr-3"></span>
            <span className="text-gray-200 text-lg font-medium tracking-wide">
              Empowering <span className="text-green-500">EV-Ready Living.</span>
            </span>
          </div>
          <div className="font-display text-white text-3xl font-extrabold leading-tight text-left drop-shadow-2xl">
            The Future of EV Charging is Mobile.
          </div>
        </div>
      </div>

      {/* Right — login form */}
      <div className="flex w-[40%] items-center justify-center bg-white p-10">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-10 text-center">
            <h2 className="font-display m-0 mb-2 text-4xl font-extrabold text-primary">
              ChargeHub
            </h2>
            <p className="text-gray-600 text-base">
              Employee Login
            </p>
          </div>

          {/* Success message after password change */}
          {passwordChangedMessage && (
            <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2" role="status" aria-live="polite">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-green-800 text-sm">Password changed successfully. Please sign in with your new password.</p>
            </div>
          )}
          {/* Error Message */}
          {error && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between" role="alert" aria-live="assertive">
              <div className="flex items-start flex-1">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800 ml-2 flex-shrink-0"
                aria-label="Close error message"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {tempToken ? (
            <div>
              <div className="mb-4 flex items-center gap-2 text-gray-700">
                <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
                <span className="font-medium">Two-factor authentication</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                {useBackupCode
                  ? 'Enter one of your backup codes (8–10 characters).'
                  : 'Enter the 6-digit code from your authenticator app.'}
              </p>
              <div className="mb-4">
                <Input
                  type="text"
                  inputMode={useBackupCode ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  placeholder={useBackupCode ? 'e.g. ABC12345' : '000000'}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(useBackupCode ? /[^A-Za-z0-9]/g : /\D/g, '').slice(0, useBackupCode ? 10 : 6))}
                  className="h-12 text-center text-lg tracking-widest font-mono"
                  maxLength={useBackupCode ? 10 : 6}
                  aria-label={useBackupCode ? 'Backup code' : 'Verification code'}
                />
              </div>
              <button
                type="button"
                onClick={() => { setUseBackupCode(!useBackupCode); setTotpCode(''); setError(null); }}
                className="mb-4 block text-sm text-primary hover:underline"
              >
                {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
              </button>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBackFrom2FA}
                  disabled={isVerifying2FA}
                  className="flex-1 h-12"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleVerify2FA}
                  disabled={isVerifying2FA || !totpCode.trim()}
                  className="h-12 flex-1 text-base font-semibold"
                >
                  {isVerifying2FA ? 'Verifying...' : 'Verify'}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-5">
                <label className="block mb-2 text-gray-700">
                  Username
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                  <Input
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 h-12"
                    autoComplete="username"
                    maxLength={255}
                    aria-required="true"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-2 text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                  <Input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12"
                    autoComplete="current-password"
                    maxLength={128}
                    aria-required="true"
                  />
                </div>
              </div>
              <div className="mb-6 text-right">
                <Link href="/forgot-password" className="text-sm text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>

              <Button
                onClick={handleLogin}
                disabled={isLoading}
                className="h-12 w-full text-base font-semibold"
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
